import { useState, useCallback, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Plus, CheckCircle, XCircle, FileText,
  Upload, Loader2, Trash2, AlertTriangle, Search, List, Link2, X, FileDown, Eye, ExternalLink,
  Building2, ChevronDown, ChevronRight, RefreshCw, ClipboardCheck,
  Clock, Send, ThumbsUp, ThumbsDown, ShieldCheck, Printer, FileSpreadsheet
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { OCNumber } from '../lib/ocFormat';
import { exportToCSV, triggerPrint } from '../lib/printUtils';
import { DocRefLink } from '../components/DocRefLink';
import { fmtFecha } from '../lib/utils'; // Iter 292.g — formato DD-MM-YYYY

const fmt = (num) => Math.round(num || 0).toLocaleString('es-CL');
const _formatDate = (d) => d ? d.replace(' 0', '').trim() : '-';

// =========== TAB: POR OC (manual entry) ===========
function TabPorOC({ onViewDocument }) {
  const [ocInput, setOcInput] = useState('');
  const [ocList, setOcList] = useState([]);
  const [loadingOC, setLoadingOC] = useState(false);
  const [error, setError] = useState(null);
  const [dteModal, setDteModal] = useState({ open: false, ocIndex: null });
  const [dteSearchInput, setDteSearchInput] = useState('');
  const [dteSearchLoading, setDteSearchLoading] = useState(false);
  const [dteSearchResult, setDteSearchResult] = useState(null);
  const [dteSearchError, setDteSearchError] = useState(null);
  const [contabilizandoTodas, setContabilizandoTodas] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, results: [] });
  const [showBulkResult, setShowBulkResult] = useState(false);
  const [confirmSingle, setConfirmSingle] = useState({ open: false, idx: null });
  const [singleLoading, setSingleLoading] = useState(false);
  const [singleResult, setSingleResult] = useState(null);

  const handleAddOC = useCallback(async () => {
    const num = parseInt(ocInput);
    if (!num || isNaN(num)) return;
    if (ocList.find(o => o.numero_oc === num)) { setError(`OC ${num} ya esta en la lista`); setOcInput(''); return; }
    setLoadingOC(true); setError(null);
    try {
      const res = await api.get(`/alegra/buscar-oc/${num}`);
      let dteSuggestions = [], autodte = null;
      try {
        const dteRes = await api.get(`/alegra/buscar-dte-para-oc/${num}`);
        dteSuggestions = dteRes.data.dtes || [];
        const exactMatch = dteSuggestions.find(d => d.match_type === 'oc_ref' && !d.ya_usada);
        if (exactMatch) autodte = exactMatch;
      } catch (_e) { /* empty */ }
      setOcList(prev => [...prev, { ...res.data, dte: autodte, dteSuggestions }]);
      setOcInput('');
    } catch (err) { setError(err.response?.data?.detail || `Error al buscar OC ${num}`); }
    finally { setLoadingOC(false); }
  }, [ocInput, ocList]);

  const handleKeyPress = (e) => { if (e.key === 'Enter') handleAddOC(); };
  const removeOC = (num) => setOcList(prev => prev.filter(o => o.numero_oc !== num));
  const clearAll = () => { setOcList([]); setError(null); };
  const selectDTE = (i, dte) => setOcList(prev => prev.map((o, idx) => idx === i ? { ...o, dte } : o));
  const removeDTE = (i) => setOcList(prev => prev.map((o, idx) => idx === i ? { ...o, dte: null } : o));
  const openDteSearch = (i) => { setDteModal({ open: true, ocIndex: i }); setDteSearchInput(''); setDteSearchResult(null); setDteSearchError(null); };

  const handleDteSearch = async () => {
    if (!dteSearchInput.trim()) return;
    setDteSearchLoading(true); setDteSearchError(null); setDteSearchResult(null);
    try { const res = await api.get(`/alegra/buscar-dte-por-folio/${dteSearchInput.trim()}`); setDteSearchResult(res.data); }
    catch (err) { setDteSearchError(err.response?.data?.detail || 'DTE no encontrado'); }
    finally { setDteSearchLoading(false); }
  };
  const confirmDteSelection = () => { if (dteSearchResult && dteModal.ocIndex !== null) { selectDTE(dteModal.ocIndex, dteSearchResult); setDteModal({ open: false, ocIndex: null }); } };
  const refreshOC = async (num) => { try { const res = await api.get(`/alegra/buscar-oc/${num}`); setOcList(prev => prev.map(o => o.numero_oc === num ? { ...o, ...res.data } : o)); } catch (_e) { /* empty */ } };
  const openSingleConfirm = (idx) => { setConfirmSingle({ open: true, idx }); setSingleResult(null); };

  const handleContabilizarSingle = async () => {
    const oc = ocList[confirmSingle.idx];
    if (!oc || !oc.dte || !oc.recepciones?.[0]) return;
    setSingleLoading(true); setSingleResult(null);
    try {
      const res = await api.post('/alegra/contabilizar-oc', { numero_oc: oc.numero_oc, numero_recepcion: oc.recepciones[0].numero_recepcion, dte_folio: oc.dte.folio, dte_rut_emisor: oc.dte.emisor_rut });
      setSingleResult({ type: 'success', text: res.data.message }); refreshOC(oc.numero_oc);
      setTimeout(() => setConfirmSingle({ open: false, idx: null }), 2000);
    } catch (err) { setSingleResult({ type: 'error', text: err.response?.data?.detail || 'Error' }); }
    finally { setSingleLoading(false); }
  };

  const handleContabilizarTodas = async () => {
    const pendientesList = ocList.filter(o => o.tiene_recepcion && !o.contabilizada && o.dte && o.recepciones?.length > 0);
    if (!pendientesList.length) return;
    setContabilizandoTodas(true); setBulkProgress({ current: 0, total: pendientesList.length, results: [] }); setShowBulkResult(true);
    const results = [];
    for (let i = 0; i < pendientesList.length; i++) {
      const oc = pendientesList[i]; setBulkProgress(prev => ({ ...prev, current: i + 1 }));
      try {
        const res = await api.post('/alegra/contabilizar-oc', { numero_oc: oc.numero_oc, numero_recepcion: oc.recepciones[0].numero_recepcion, dte_folio: oc.dte.folio, dte_rut_emisor: oc.dte.emisor_rut });
        results.push({ numero_oc: oc.numero_oc, success: true, message: res.data.message }); refreshOC(oc.numero_oc);
      } catch (err) { results.push({ numero_oc: oc.numero_oc, success: false, message: err.response?.data?.detail || 'Error' }); }
      setBulkProgress(prev => ({ ...prev, results: [...results] }));
    }
    setContabilizandoTodas(false);
  };

  const pendientes = ocList.filter(o => o.tiene_recepcion && !o.contabilizada && o.dte).length;
  const sinDte = ocList.filter(o => o.tiene_recepcion && !o.contabilizada && !o.dte).length;
  const contabilizadas = ocList.filter(o => o.contabilizada).length;

  return (
    <>
      <div className="bg-white border rounded-lg p-4">
        <div className="flex gap-3 items-end">
          <div className="flex-1 max-w-xs">
            <label className="block text-sm font-medium mb-1">N. Orden de Compra</label>
            <Input type="number" placeholder="Ej: 18077" value={ocInput} onChange={(e) => setOcInput(e.target.value)} onKeyPress={handleKeyPress} data-testid="add-oc-input" />
          </div>
          <Button onClick={handleAddOC} disabled={loadingOC || !ocInput} data-testid="add-oc-btn">
            {loadingOC ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Agregar
          </Button>
          {ocList.length > 0 && <Button variant="outline" onClick={clearAll} className="text-red-600"><Trash2 className="w-4 h-4 mr-1" /> Limpiar</Button>}
        </div>
        {error && <p className="mt-2 text-sm text-red-600" data-testid="error-msg">{error}</p>}
      </div>

      {ocList.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white border rounded p-3 text-center"><p className="text-xs text-muted-foreground">OCs en Lista</p><p className="text-xl font-bold">{ocList.length}</p></div>
          <div className="bg-green-50 border border-green-200 rounded p-3 text-center"><p className="text-xs text-green-700">Listas (con DTE)</p><p className="text-xl font-bold text-green-700" data-testid="pendientes-count">{pendientes}</p></div>
          <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-center"><p className="text-xs text-yellow-700">Sin DTE</p><p className="text-xl font-bold text-yellow-700">{sinDte}</p></div>
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-center"><p className="text-xs text-blue-700">Contabilizadas</p><p className="text-xl font-bold text-blue-700">{contabilizadas}</p></div>
          <div className="bg-gray-50 border rounded p-3 text-center"><p className="text-xs text-muted-foreground">Sin Recepcion</p><p className="text-xl font-bold text-gray-400">{ocList.filter(o => !o.tiene_recepcion).length}</p></div>
        </div>
      )}

      {pendientes > 0 && (
        <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 flex items-center justify-between">
          <div><p className="font-bold text-green-800">{pendientes} OC lista{pendientes > 1 ? 's' : ''} para contabilizar</p><p className="text-sm text-green-600">Con recepcion y DTE asociado</p></div>
          <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8 h-12 text-base" onClick={handleContabilizarTodas} disabled={contabilizandoTodas} data-testid="contabilizar-todas-btn">
            {contabilizandoTodas ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Upload className="w-5 h-5 mr-2" />} Contabilizar Todas ({pendientes})
          </Button>
        </div>
      )}

      {ocList.length > 0 && (
        <div className="border rounded-lg" style={{ overflowX: 'clip' }}>
            <table className="w-full text-xs" data-testid="oc-list-table">
              <thead className="sticky top-14 lg:top-0 z-20"><tr className="bg-gray-800 text-white text-left">
                <th className="p-2">N. OC</th><th className="p-2">OT</th><th className="p-2">Proveedor</th><th className="p-2 text-right">Neto OC</th>
                <th className="p-2 text-center">Recepcion</th><th className="p-2 text-center">Doc</th><th className="p-2">DTE / Factura</th>
                <th className="p-2 text-center">Estado</th><th className="p-2 text-center">Accion</th><th className="p-2 w-8"></th>
              </tr></thead>
              <tbody>
                {ocList.map((oc, idx) => (
                  <tr key={oc.numero_oc} className={`border-t ${oc.contabilizada ? 'bg-green-50' : oc.dte ? 'bg-blue-50' : oc.tiene_recepcion ? 'bg-yellow-50' : 'bg-white'}`} data-testid={`oc-row-${oc.numero_oc}`}>
                    <td className="p-2 font-mono font-bold text-blue-700"><OCNumber oc={oc} size="sm" /></td>
                    <td className="p-2"><span className="font-medium">{oc.ot_numero || '-'}</span></td>
                    <td className="p-2 truncate max-w-[150px]" title={oc.nombre_proveedor}>{oc.nombre_proveedor || oc.rut_proveedor}</td>
                    <td className="p-2 text-right font-mono">${fmt(oc.neto)}</td>
                    <td className="p-2 text-center">{oc.tiene_recepcion ? <span className="inline-flex items-center gap-0.5 text-green-700"><CheckCircle className="w-3.5 h-3.5" /> Si</span> : <span className="text-gray-400">No</span>}</td>
                    <td className="p-2 text-center">
                      {oc.recepciones?.some(r => r.ruta_archivo) ? oc.recepciones.filter(r => r.ruta_archivo).map((r, ri) => {
                        const fn = r.ruta_archivo.split('\\').pop().split('/').pop();
                        return <button key={ri} onClick={() => onViewDocument(fn)} className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 text-[9px] bg-blue-50 px-1.5 py-0.5 rounded" data-testid={`doc-link-${oc.numero_oc}-${ri}`}><Eye className="w-3 h-3" /></button>;
                      }) : <span className="text-gray-300 text-[10px]">-</span>}
                    </td>
                    <td className="p-2">
                      {oc.contabilizada ? <span className="text-green-700 text-[10px]">Contabilizada</span>
                       : oc.dte ? (
                        <div className="flex items-center gap-1">
                          <div className="bg-green-100 border border-green-300 rounded px-2 py-0.5">
                            <span className="font-mono font-bold text-green-800 text-[11px]">F. {oc.dte.folio}</span>
                            <span className="text-[10px] text-green-600 ml-1">${fmt(oc.dte.total || oc.dte.neto)}</span>
                          </div>
                          <button onClick={() => removeDTE(idx)} className="p-0.5 hover:bg-red-100 rounded"><X className="w-3 h-3 text-red-400" /></button>
                        </div>
                      ) : oc.tiene_recepcion ? (
                        <div className="space-y-1">
                          {oc.dteSuggestions?.filter(d => !d.ya_usada).length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {oc.dteSuggestions.filter(d => !d.ya_usada).slice(0, 3).map(d => (
                                <button key={d.folio} onClick={() => selectDTE(idx, d)} className="bg-blue-100 hover:bg-blue-200 border border-blue-300 rounded px-1.5 py-0.5 text-[10px] text-blue-700"><Link2 className="w-2.5 h-2.5 inline mr-0.5" />F.{d.folio}</button>
                              ))}
                              <button onClick={() => openDteSearch(idx)} className="text-[10px] text-blue-600 underline">Buscar otro</button>
                            </div>
                          ) : <Button variant="outline" size="sm" className="h-5 text-[10px]" onClick={() => openDteSearch(idx)}><Search className="w-3 h-3 mr-1" /> Buscar DTE</Button>}
                        </div>
                      ) : <span className="text-[10px] text-gray-400">-</span>}
                    </td>
                    <td className="p-2 text-center">
                      {oc.contabilizada ? <span className="inline-flex items-center gap-0.5 text-green-700 text-[10px]"><CheckCircle className="w-3.5 h-3.5" /> En Alegra</span>
                       : !oc.tiene_recepcion ? <span className="text-[10px] text-gray-400">Sin recepcion</span>
                       : !oc.dte ? <span className="inline-flex items-center gap-0.5 text-yellow-600 text-[10px]"><AlertTriangle className="w-3.5 h-3.5" /> Falta DTE</span>
                       : <span className="inline-flex items-center gap-0.5 text-green-600 text-[10px]"><CheckCircle className="w-3.5 h-3.5" /> Lista</span>}
                    </td>
                    <td className="p-2 text-center">
                      {oc.tiene_recepcion && !oc.contabilizada && oc.dte && <Button size="sm" className="h-6 text-[10px] bg-green-600 hover:bg-green-700" onClick={() => openSingleConfirm(idx)} data-testid={`contabilizar-${oc.numero_oc}`}><Upload className="w-3 h-3 mr-1" /> Contabilizar</Button>}
                      {oc.contabilizada && <span className="text-[10px] text-green-600">ID:{oc.alegra_id}</span>}
                    </td>
                    <td className="p-2"><button onClick={() => removeOC(oc.numero_oc)} className="p-1 hover:bg-red-100 rounded"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
        </div>
      )}

      {ocList.length === 0 && (
        <div className="text-center py-16 bg-gray-50 rounded-lg">
          <Search className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-600">Agregue ordenes de compra</h3>
          <p className="text-sm text-muted-foreground mt-1">Escriba el N. OC para agregarlo. El DTE se buscara automaticamente.</p>
        </div>
      )}

      {/* DTE Search Modal */}
      <Dialog open={dteModal.open} onOpenChange={(open) => setDteModal({ open, ocIndex: open ? dteModal.ocIndex : null })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Buscar DTE / Factura</DialogTitle><DialogDescription>Ingrese el numero de folio</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input placeholder="Numero de folio" value={dteSearchInput} onChange={(e) => setDteSearchInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleDteSearch()} data-testid="dte-folio-input" />
              <Button onClick={handleDteSearch} disabled={dteSearchLoading}>{dteSearchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</Button>
            </div>
            {dteSearchError && <p className="text-sm text-red-600">{dteSearchError}</p>}
            {dteSearchResult && (
              <div className={`rounded-lg p-3 text-sm border ${dteSearchResult.ya_usada ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                <div className="flex justify-between"><span className="text-muted-foreground">Folio:</span><span className="font-bold">{dteSearchResult.folio}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Emisor:</span><span>{dteSearchResult.emisor_razon_social}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total:</span><span className="font-bold">${fmt(dteSearchResult.total)}</span></div>
                {dteSearchResult.ya_usada && <p className="mt-2 text-red-600 font-medium flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Ya asociado a OC {dteSearchResult.oc_asociada}</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDteModal({ open: false, ocIndex: null })}>Cancelar</Button>
            {dteSearchResult && !dteSearchResult.ya_usada && <Button onClick={confirmDteSelection} className="bg-green-600 hover:bg-green-700" data-testid="confirm-dte-btn"><Link2 className="w-4 h-4 mr-1" /> Asociar DTE</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single Contabilizar Confirm */}
      <Dialog open={confirmSingle.open} onOpenChange={(open) => !singleLoading && setConfirmSingle({ open, idx: open ? confirmSingle.idx : null })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Contabilizar en Alegra</DialogTitle><DialogDescription>Se creara una factura de compra en Alegra</DialogDescription></DialogHeader>
          {confirmSingle.idx !== null && ocList[confirmSingle.idx] && (() => { const oc = ocList[confirmSingle.idx]; return (
            <div className="space-y-3 py-2">
              <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">OC:</span><span className="font-bold">{oc.numero_oc}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Proveedor:</span><span>{oc.nombre_proveedor}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Neto OC:</span><span className="font-bold">${fmt(oc.neto)}</span></div>
              </div>
              {oc.dte && <div className="bg-blue-50 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">DTE Folio:</span><span className="font-bold">{oc.dte.folio}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total DTE:</span><span className="font-bold text-green-700">${fmt(oc.dte.total)}</span></div>
              </div>}
              {singleResult && <div className={`rounded-lg p-3 text-sm ${singleResult.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{singleResult.text}</div>}
            </div>
          );})()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSingle({ open: false, idx: null })} disabled={singleLoading}>Cancelar</Button>
            <Button onClick={handleContabilizarSingle} disabled={singleLoading || singleResult?.type === 'success'} className="bg-green-600 hover:bg-green-700" data-testid="confirm-contabilizar-btn">
              {singleLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Contabilizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Result Modal */}
      <Dialog open={showBulkResult} onOpenChange={(open) => !contabilizandoTodas && setShowBulkResult(open)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Contabilizacion en Alegra</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2 max-h-[300px] overflow-auto">
            {contabilizandoTodas && <div className="flex items-center gap-2 text-sm text-blue-700"><Loader2 className="w-4 h-4 animate-spin" /> Procesando {bulkProgress.current}/{bulkProgress.total}...</div>}
            {bulkProgress.results.map((r, i) => (
              <div key={i} className={`flex items-center gap-2 p-2 rounded text-sm ${r.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {r.success ? <CheckCircle className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
                <span className="font-mono font-bold">OC {r.numero_oc}:</span><span className="truncate">{r.message}</span>
              </div>
            ))}
          </div>
          {!contabilizandoTodas && bulkProgress.results.length > 0 && <DialogFooter><Button onClick={() => setShowBulkResult(false)}>Cerrar</Button></DialogFooter>}
        </DialogContent>
      </Dialog>
    </>
  );
}


// =========== ESTADO BADGE ===========
function EstadoBadge({ estado }) {
  const config = {
    contabilizada: { bg: 'bg-blue-100 text-blue-800 border-blue-300', icon: <CheckCircle className="w-3 h-3" />, label: 'En Alegra' },
    pendiente_contabilizar: { bg: 'bg-amber-100 text-amber-800 border-amber-300', icon: <Clock className="w-3 h-3" />, label: 'Pend. Contab.' },
    en_lista_vb: { bg: 'bg-purple-100 text-purple-800 border-purple-300', icon: <ShieldCheck className="w-3 h-3" />, label: 'En Lista VB' },
  };
  const c = config[estado] || config.pendiente_contabilizar;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-medium ${c.bg}`}>{c.icon} {c.label}</span>;
}


// =========== TAB: ESTADO CUENTA PROVEEDOR ===========
function TabEstadoCuenta({ onViewDocument }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('todos');

  // Selection for VB list
  const [selected, setSelected] = useState({}); // key: "oc|docmto" => factura obj
  const [creandoLista, setCreandoLista] = useState(false);
  const [listaResult, setListaResult] = useState(null);

  // VB Gerencia lists
  const [listas, setListas] = useState([]);
  const [listasLoading, setListasLoading] = useState(false);
  const [showListas, setShowListas] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/contabilidad/estado-cuenta-proveedores');
      setData(res.data);
    } catch (err) { setError(err.response?.data?.detail || 'Error al cargar estado de cuenta'); }
    finally { setLoading(false); }
  }, []);

  const fetchListas = useCallback(async () => {
    setListasLoading(true);
    try {
      const res = await api.get('/contabilidad/listas-contabilizar');
      setListas(res.data.listas || []);
    } catch (_e) { /* empty */ }
    finally { setListasLoading(false); }
  }, []);

  useEffect(() => { fetchData(); fetchListas(); }, [fetchData, fetchListas]);

  const toggleExpand = (rut) => setExpanded(prev => ({ ...prev, [rut]: !prev[rut] }));
  const expandAll = () => { if (!data?.proveedores) return; const all = {}; data.proveedores.forEach(p => { all[p.rut_proveedor] = true; }); setExpanded(all); };
  const collapseAll = () => setExpanded({});

  // ============== IMPRIMIR ESTADO CUENTA PROVEEDOR (Carlos 08/05/2026) ==============
  const imprimirEstadoCuenta = (prov) => {
    if (!prov) return;
    const facturas = prov.facturas || [];
    const escapeHtml = (str) => String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const totalFacturado = facturas.reduce((s, f) => s + (Number(f.total) || 0), 0);

    // Carlos (Jun 2026): en la impresión, ordenar los documentos del MÁS ANTIGUO
    // al MÁS NUEVO por Fecha de Emisión.
    const parseFechaEC = (s) => {
      if (!s) return 0;
      const str = String(s).trim().slice(0, 10);
      let m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return new Date(+m[1], +m[2] - 1, +m[3]).getTime();
      m = str.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
      if (m) return new Date(+m[3], +m[2] - 1, +m[1]).getTime();
      const t = Date.parse(str);
      return Number.isNaN(t) ? 0 : t;
    };
    const facturasOrdenadas = [...facturas].sort(
      (a, b) => parseFechaEC(a.fecha_emision) - parseFechaEC(b.fecha_emision),
    );

    const rows = facturasOrdenadas.map((f) => {
      const venc = f.fecha_vencimiento || '';
      const isVencida = (Number(f.saldo) || 0) > 0 && venc && new Date(venc) < new Date();
      const estadoLabel = (f.estado || '').replace(/_/g, ' ').toUpperCase();
      const estadoBg = (f.estado === 'contabilizada' || f.fuente === 'alegra')
        ? '#d1fae5;color:#047857'
        : f.estado === 'en_lista_vb'
          ? '#e9d8fd;color:#553c9a'
          : '#fef3c7;color:#b45309';
      const ocCell = f.numero_oc
        ? `<span style="color:#047857;font-family:monospace;">${escapeHtml(f.numero_oc)}</span>`
        : '<span style="color:#999;">Sin OC</span>';
      const recepCell = f.estado_recepcion === 'Recepcionada'
        ? '<span class="badge" style="background:#d1fae5;color:#047857;">SÍ</span>'
        : f.estado_recepcion === 'Pendiente'
          ? '<span class="badge" style="background:#fef3c7;color:#b45309;">PEND.</span>'
          : '<span style="color:#999;">-</span>';
      return `<tr>
        <td class="c"><span class="badge" style="background:${estadoBg}">${estadoLabel || 'PEND.'}</span></td>
        <td class="mono" style="color:#1d4ed8;">${escapeHtml(f.numero_ref || f.docmto || '-')}</td>
        <td>${fmtFecha(f.fecha_emision) || '-'}</td>
        <td class="${isVencida ? 'venc' : ''}">${venc || '-'}</td>
        <td class="r mono">$${fmt(f.total)}</td>
        <td class="r mono ${(Number(f.saldo) || 0) > 0 ? 'venc' : 'paid'}">$${fmt(f.saldo)}</td>
        <td class="c">${ocCell}</td>
        <td class="c">${recepCell}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html lang="es"><head>
<meta charset="utf-8"/>
<title>Estado Cuenta ${escapeHtml(prov.nombre_proveedor || '')} - CHIRINO STEEL</title>
<style>
  @page { size: letter; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial,sans-serif; color:#1a202c; font-size:10px; margin:0; padding:8px; }
  .header { text-align:center; border-bottom:3px solid #1a365d; padding-bottom:8px; margin-bottom:12px; }
  .header h1 { font-size:16px; color:#1a365d; margin:0 0 4px; }
  .header h2 { font-size:13px; color:#2d3748; margin:0; }
  .header p { font-size:9px; color:#4a5568; margin:2px 0 0; }
  .summary { display:flex; gap:6px; margin:8px 0 12px; }
  .summary .box { flex:1; text-align:center; padding:6px; border:1px solid #cbd5e0; background:#f7fafc; border-radius:4px; }
  .summary .box.hl { background:#e9d8fd; }
  .summary .label { font-size:7px; color:#4a5568; text-transform:uppercase; }
  .summary .value { font-size:11px; font-weight:bold; font-family:monospace; margin-top:2px; }
  table { width:100%; border-collapse:collapse; font-size:9px; }
  th { background:#1a365d; color:#fff; padding:4px 5px; border:1px solid #1a365d; text-align:left; font-size:8px; text-transform:uppercase; }
  td { padding:3px 5px; border:1px solid #cbd5e0; vertical-align:top; }
  tbody tr:nth-child(even) { background:#fafbfc; }
  .r { text-align:right; }
  .c { text-align:center; }
  .mono { font-family:monospace; }
  .venc { color:#b91c1c; font-weight:600; }
  .paid { color:#15803d; }
  .badge { display:inline-block; padding:1px 6px; border-radius:6px; font-size:7px; font-weight:600; white-space:nowrap; }
  .totals td { background:#1a365d !important; color:#fff !important; font-weight:bold; padding:5px; }
  .footer { text-align:center; font-size:7px; color:#718096; margin-top:10px; border-top:1px solid #e2e8f0; padding-top:5px; }
  .toolbar { background:#1e3a8a; color:#fff; padding:6px 10px; display:flex; justify-content:space-between; align-items:center; margin-bottom:6px; border-radius:4px; }
  .toolbar button { background:#fff; color:#1e3a8a; border:0; padding:4px 12px; border-radius:3px; font-weight:600; cursor:pointer; margin-left:6px; }
  .toolbar button:hover { background:#fef3c7; }
  @media print { .toolbar { display:none !important; } body { padding:0; } }
</style>
</head>
<body>
  <div class="toolbar"><span><b>CHIRINO STEEL</b> · Estado Cuenta ${escapeHtml(prov.nombre_proveedor || '')}</span><span><button onclick="window.print()">🖨️ Imprimir</button><button onclick="window.close()">✕ Cerrar</button></span></div>
  <div class="header">
    <h1>CHIRINO STEEL ING. Y CONT. SPA</h1>
    <h2>ESTADO DE CUENTA · ${escapeHtml(prov.nombre_proveedor || '')}</h2>
    <p>RUT: <b>${prov.rut_proveedor || '-'}</b> · ${facturas.length} factura${facturas.length === 1 ? '' : 's'}</p>
  </div>
  <div class="summary">
    <div class="box"><div class="label">Saldo Alegra</div><div class="value" style="color:#1d4ed8;">$${fmt(prov.saldo_alegra)}</div></div>
    <div class="box"><div class="label">Pend. Contab.</div><div class="value" style="color:#c2410c;">$${fmt(prov.monto_pendiente_contab)}</div></div>
    <div class="box"><div class="label">En Lista VB</div><div class="value" style="color:#553c9a;">$${fmt(prov.monto_en_lista || 0)}</div></div>
    <div class="box hl"><div class="label">Total Deuda</div><div class="value" style="color:#b91c1c;">$${fmt(prov.total_deuda)}</div></div>
  </div>
  <table>
    <thead><tr>
      <th style="width:65px;text-align:center;">Estado</th>
      <th style="width:80px;">N. Factura</th>
      <th style="width:75px;">F. Emisión</th>
      <th style="width:75px;">F. Vencimiento</th>
      <th style="width:80px;text-align:right;">Total</th>
      <th style="width:80px;text-align:right;">Saldo</th>
      <th style="width:65px;text-align:center;">OC</th>
      <th style="width:55px;text-align:center;">Recepción</th>
    </tr></thead>
    <tbody>
      ${rows || '<tr><td colspan="8" style="text-align:center;color:#999;padding:10px;">Sin facturas</td></tr>'}
      <tr class="totals">
        <td colspan="4" style="text-align:right;">TOTALES</td>
        <td class="r mono">$${fmt(totalFacturado)}</td>
        <td class="r mono">$${fmt(prov.total_deuda)}</td>
        <td colspan="2"></td>
      </tr>
    </tbody>
  </table>
  <div class="footer">Generado por Sistema de Gestion CHIRINO STEEL · ${new Date().toLocaleString('es-CL')}</div>
</body></html>`;

    // Estrategia robusta cross-browser via triggerPrint (detecta iOS/iPadOS
    // y abre nueva pestaña; en desktop usa iframe oculto same-origin).
    // triggerPrint YA dispara la impresión — no incluir <script>window.print()</script>
    // inline en el HTML o se imprime dos veces (Carlos, Jun 2026).
    triggerPrint(html);
  };

  // ============== EXPORTAR ESTADO CUENTA A EXCEL/CSV (Carlos 09/05/2026) ==============
  const exportarEstadoCuentaExcel = (prov) => {
    if (!prov) return;
    const facturas = prov.facturas || [];

    exportToCSV({
      filename: `estado_cuenta_${(prov.nombre_proveedor || prov.rut_proveedor || 'proveedor').replace(/\s+/g, '_').replace(/[^\w]/g, '')}`,
      title: `CHIRINO STEEL · Estado de Cuenta · ${prov.nombre_proveedor || ''}`,
      summary: [
        { label: 'RUT proveedor', value: prov.rut_proveedor || '-' },
        { label: 'Total facturas', value: facturas.length },
        { label: 'Saldo en Alegra (CLP)', value: Math.round(prov.saldo_alegra || 0) },
        { label: 'Pendiente contabilizar (CLP)', value: Math.round(prov.monto_pendiente_contab || 0) },
        { label: 'En lista VB (CLP)', value: Math.round(prov.monto_en_lista || 0) },
        { label: 'Total deuda (CLP)', value: Math.round(prov.total_deuda || 0) },
      ],
      columns: [
        { key: 'estado', label: 'Estado', format: (v) => (v || '').replace(/_/g, ' ').toUpperCase() },
        { key: 'fuente', label: 'Fuente' },
        { key: 'numero_ref', label: 'N. Factura', format: (v, r) => v || r.docmto || '' },
        { key: 'fecha_emision', label: 'Fecha Emisión' },
        { key: 'fecha_vencimiento', label: 'Fecha Vencimiento' },
        { key: 'total', label: 'Total (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'saldo', label: 'Saldo (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'numero_oc', label: 'N. OC', format: (v) => v || '' },
        { key: 'estado_oc', label: 'Estado OC' },
        { key: 'estado_recepcion', label: 'Recepción' },
        { key: 'forma_pago', label: 'Forma Pago' },
      ],
      rows: facturas,
    });
  };

  // Selection
  const toggleSelect = (f) => {
    const key = `${f.numero_oc}|${f.docmto}`;
    setSelected(prev => {
      const next = { ...prev };
      if (next[key]) delete next[key]; else next[key] = f;
      return next;
    });
  };
  const selectedCount = Object.keys(selected).length;
  const selectedTotal = Object.values(selected).reduce((s, f) => s + (f.total || f.dte_total || f.total_neto || 0), 0);
  const clearSelection = () => setSelected({});

  const handleCrearLista = async () => {
    const items = Object.values(selected).map(f => ({
      numero_oc: f.numero_oc || 0,
      docmto: f.docmto || f.numero_ref || '',
      dte_folio: f.dte_folio || f.docmto || f.numero_ref || '',
      dte_rut_emisor: f.dte_emisor_rut || '',
      nombre_proveedor: f.dte_emisor_razon || '',
      total_neto: f.total_neto || f.total || 0,
      dte_total: f.total || f.dte_total || 0,
      numero_recepcion: f.numero_recepcion || '',
    }));
    setCreandoLista(true); setListaResult(null);
    try {
      const res = await api.post('/contabilidad/listas-contabilizar', { items });
      setListaResult({ type: 'success', text: `Lista ${res.data.lista_id} creada. ${res.data.message}` });
      setSelected({}); fetchData(); fetchListas();
    } catch (err) { setListaResult({ type: 'error', text: err.response?.data?.detail || 'Error al crear lista' }); }
    finally { setCreandoLista(false); }
  };

  // Gerencia actions
  const handleAprobar = async (listaId) => {
    setActionLoading(listaId);
    try { await api.put(`/contabilidad/listas-contabilizar/${listaId}/aprobar`); fetchListas(); fetchData(); }
    catch (e) { alert(e.response?.data?.detail || 'Error'); }
    finally { setActionLoading(null); }
  };
  const handleRechazar = async (listaId) => {
    setActionLoading(listaId);
    try { await api.put(`/contabilidad/listas-contabilizar/${listaId}/rechazar`); fetchListas(); fetchData(); }
    catch (e) { alert(e.response?.data?.detail || 'Error'); }
    finally { setActionLoading(null); }
  };
  const handleEliminar = async (listaId) => {
    setActionLoading(listaId);
    try { await api.delete(`/contabilidad/listas-contabilizar/${listaId}`); fetchListas(); fetchData(); }
    catch (e) { alert(e.response?.data?.detail || 'Error'); }
    finally { setActionLoading(null); }
  };

  const handleContabilizarAlegra = async (listaId) => {
    if (!window.confirm('¿Enviar esta lista a Alegra para contabilizar? Se crearán las facturas de compra en Alegra.')) return;
    setActionLoading(listaId);
    try {
      const res = await api.post(`/contabilidad/listas-contabilizar/${listaId}/contabilizar-alegra`);
      const d = res.data;
      const avisos = (d.resultados || []).filter(r => r.avisos?.length).map(r => `Folio ${r.folio}: ${r.avisos.join(' ')}`);
      if (d.errores > 0) {
        alert(`Contabilización parcial: ${d.exitosos} OK, ${d.errores} errores.\n\n${d.resultados.filter(r => r.status === 'error').map(r => `Folio ${r.folio}: ${r.error}`).join('\n')}${avisos.length ? '\n\n⚠ ' + avisos.join('\n⚠ ') : ''}`);
      } else {
        alert(`Contabilización exitosa: ${d.exitosos} facturas enviadas a Alegra.${avisos.length ? '\n\n⚠ ' + avisos.join('\n⚠ ') : ''}`);
      }
      fetchListas(); fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error contabilizando en Alegra');
    } finally { setActionLoading(null); }
  };

  const filteredProveedores = (data?.proveedores || []).filter(p => {
    const matchSearch = !searchTerm || p.nombre_proveedor.toLowerCase().includes(searchTerm.toLowerCase()) || p.rut_proveedor.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchSearch) return false;
    if (filterEstado === 'contabilizada') return p.contabilizadas_count > 0;
    if (filterEstado === 'pendiente') return p.pendientes_count > 0;
    if (filterEstado === 'en_lista') return p.en_lista_count > 0;
    return true;
  });

  const estadoFilters = [
    { val: 'todos', label: 'Todos' },
    { val: 'contabilizada', label: 'En Alegra' },
    { val: 'pendiente', label: 'Pend. Contab.' },
    { val: 'en_lista', label: 'En Lista VB' },
  ];

  const pendientesListas = listas.filter(l => l.estado === 'pendiente_vb');
  const _aprobadasListas = listas.filter(l => l.estado === 'aprobada');

  return (
    <>
      {/* Toolbar */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px] max-w-sm">
            <label className="block text-xs font-medium mb-1 text-gray-600">Buscar proveedor</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Nombre o RUT..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="search-proveedor-input" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Estado</label>
            <div className="flex gap-1">
              {estadoFilters.map(opt => (
                <button key={opt.val} onClick={() => setFilterEstado(opt.val)}
                  className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${filterEstado === opt.val ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                  data-testid={`filter-estado-${opt.val}`}>{opt.label}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={expandAll} title="Expandir todos"><ChevronDown className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" onClick={collapseAll} title="Colapsar todos"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <Button variant="outline" onClick={() => setShowListas(!showListas)} className={`relative ${pendientesListas.length > 0 ? 'border-purple-400 text-purple-700' : ''}`} data-testid="toggle-listas-btn">
            <ClipboardCheck className="w-4 h-4 mr-1" /> Listas VB
            {pendientesListas.length > 0 && <span className="absolute -top-1.5 -right-1.5 bg-purple-600 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">{pendientesListas.length}</span>}
          </Button>
          <Button variant="outline" onClick={() => { fetchData(); fetchListas(); }} disabled={loading} data-testid="refresh-estado-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Summary */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border rounded p-3 text-center">
            <p className="text-xs text-muted-foreground">Proveedores</p>
            <p className="text-xl font-bold" data-testid="total-proveedores">{data.resumen.total_proveedores}</p>
            <p className="text-[10px] text-muted-foreground">{data.resumen.total_facturas} facturas</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-center">
            <p className="text-xs text-blue-700">Saldo en Alegra</p>
            <p className="text-lg font-bold text-blue-700">${fmt(data.resumen.total_saldo_alegra)}</p>
            <p className="text-[10px] text-blue-600">Contabilizadas, pend. pago</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-center">
            <p className="text-xs text-amber-700">Pend. Contabilizar</p>
            <p className="text-lg font-bold text-amber-700">${fmt(data.resumen.total_pendiente_contab)}</p>
            <p className="text-[10px] text-amber-600">Con DTE, sin enviar a Alegra</p>
          </div>
          <div className="bg-gray-900 rounded p-3 text-center">
            <p className="text-xs text-gray-400">Total Deuda</p>
            <p className="text-lg font-bold text-white">${fmt(data.resumen.total_deuda)}</p>
            <p className="text-[10px] text-gray-500">Pendiente de pago total</p>
          </div>
        </div>
      )}

      {/* Selection bar */}
      {selectedCount > 0 && (
        <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-3 flex items-center justify-between" data-testid="selection-bar">
          <div className="flex items-center gap-3">
            <span className="bg-purple-600 text-white px-2.5 py-1 rounded-full text-sm font-bold">{selectedCount}</span>
            <div>
              <p className="font-medium text-purple-800 text-sm">factura{selectedCount > 1 ? 's' : ''} seleccionada{selectedCount > 1 ? 's' : ''} para contabilizar</p>
              <p className="text-xs text-purple-600">Monto total: ${fmt(selectedTotal)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={clearSelection} className="text-purple-700"><X className="w-3 h-3 mr-1" /> Limpiar</Button>
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" onClick={handleCrearLista} disabled={creandoLista} data-testid="crear-lista-btn">
              {creandoLista ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Enviar a VB Gerencia
            </Button>
          </div>
        </div>
      )}

      {listaResult && (
        <div className={`rounded-lg p-3 text-sm ${listaResult.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {listaResult.type === 'success' ? <CheckCircle className="w-4 h-4 inline mr-1" /> : <XCircle className="w-4 h-4 inline mr-1" />}
          {listaResult.text}
          <button onClick={() => setListaResult(null)} className="ml-2 underline text-xs">cerrar</button>
        </div>
      )}

      {/* VB Gerencia Lists Panel */}
      {showListas && (
        <div className="bg-white border-2 border-purple-200 rounded-lg overflow-hidden" data-testid="listas-panel">
          <div className="bg-purple-50 px-4 py-2 border-b border-purple-200 flex items-center justify-between">
            <h3 className="font-semibold text-purple-800 text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Listas de Contabilizacion - VB Gerencia</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowListas(false)}><X className="w-4 h-4" /></Button>
          </div>
          <div className="p-3 space-y-2 max-h-[300px] overflow-auto">
            {listasLoading ? <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-purple-500" /></div>
             : listas.length === 0 ? <p className="text-center text-gray-500 text-sm py-4">No hay listas creadas</p>
             : listas.map(l => (
              <div key={l.lista_id} className={`border rounded-lg p-3 text-xs ${l.estado === 'pendiente_vb' ? 'border-purple-300 bg-purple-50/50' : l.estado === 'aprobada' ? 'border-green-300 bg-green-50/50' : l.estado === 'rechazada' ? 'border-red-300 bg-red-50/50' : 'border-gray-200'}`} data-testid={`lista-${l.lista_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold">#{l.lista_id}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${l.estado === 'pendiente_vb' ? 'bg-purple-200 text-purple-800' : l.estado === 'aprobada' ? 'bg-green-200 text-green-800' : l.estado === 'rechazada' ? 'bg-red-200 text-red-800' : l.estado === 'contabilizada' ? 'bg-blue-200 text-blue-800' : l.estado === 'parcial' ? 'bg-amber-200 text-amber-800' : 'bg-gray-200 text-gray-800'}`}>
                      {l.estado === 'pendiente_vb' ? 'Pendiente VB' : l.estado === 'aprobada' ? 'Aprobada' : l.estado === 'rechazada' ? 'Rechazada' : l.estado === 'contabilizada' ? 'Contabilizada' : l.estado === 'parcial' ? 'Parcial' : l.estado}
                    </span>
                    <span className="text-gray-500">{l.total_items} items</span>
                    <span className="font-medium">${fmt(l.total_monto)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {l.estado === 'pendiente_vb' && (
                      <>
                        <Button size="sm" className="h-6 text-[10px] bg-green-600 hover:bg-green-700" onClick={() => handleAprobar(l.lista_id)} disabled={actionLoading === l.lista_id} data-testid={`aprobar-${l.lista_id}`}>
                          {actionLoading === l.lista_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ThumbsUp className="w-3 h-3 mr-0.5" />} Aprobar
                        </Button>
                        <Button size="sm" variant="outline" className="h-6 text-[10px] text-red-600 border-red-300" onClick={() => handleRechazar(l.lista_id)} disabled={actionLoading === l.lista_id} data-testid={`rechazar-${l.lista_id}`}>
                          <ThumbsDown className="w-3 h-3 mr-0.5" /> Rechazar
                        </Button>
                      </>
                    )}
                    {l.estado === 'aprobada' && (
                      <Button size="sm" className="h-6 text-[10px] bg-blue-600 hover:bg-blue-700" onClick={() => handleContabilizarAlegra(l.lista_id)} disabled={actionLoading === l.lista_id} data-testid={`contabilizar-${l.lista_id}`}>
                        {actionLoading === l.lista_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3 mr-0.5" />} Contabilizar en Alegra
                      </Button>
                    )}
                    {l.estado !== 'contabilizada' && (
                      <Button size="sm" variant="ghost" className="h-6 text-[10px] text-red-500" onClick={() => handleEliminar(l.lista_id)} disabled={actionLoading === l.lista_id}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                    {l.estado === 'contabilizada' && (
                      <span className="text-[10px] text-green-700 bg-green-100 px-2 py-0.5 rounded font-medium">Contabilizada</span>
                    )}
                    {l.estado === 'parcial' && (
                      <span className="text-[10px] text-amber-700 bg-amber-100 px-2 py-0.5 rounded font-medium">Parcial (ver errores)</span>
                    )}
                  </div>
                </div>
                <div className="text-[10px] text-gray-500 flex gap-3">
                  <span>Creada: {l.fecha_creacion?.substring(0, 16).replace('T', ' ')}</span>
                  <span>Por: {l.creado_por}</span>
                  {l.aprobado_por && <span>VB: {l.aprobado_por} ({l.fecha_aprobacion?.substring(0, 16).replace('T', ' ')})</span>}
                </div>
                {l.items && l.items.length > 0 && (
                  <div className="mt-2 space-y-0.5">
                    {l.items.map((it, ii) => (
                      <div key={ii} className="flex items-center gap-2 text-[10px] text-gray-600">
                        <span className="font-mono">OC {it.numero_oc}</span>
                        <span>F.{it.dte_folio}</span>
                        <span className="truncate">{it.nombre_proveedor}</span>
                        <span className="ml-auto font-medium">${fmt(it.dte_total || it.total_neto)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm"><AlertTriangle className="w-4 h-4 inline mr-1" /> {error}</div>}
      {loading && <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /><span className="ml-3 text-gray-600">Cargando estado de cuenta...</span></div>}

      {/* Providers accordion */}
      {!loading && filteredProveedores.length > 0 && (
        <div className="space-y-2" data-testid="proveedores-list">
          {filteredProveedores.map((prov) => {
            const isExpanded = expanded[prov.rut_proveedor];
            return (
              <div key={prov.rut_proveedor} className="bg-white border rounded-lg overflow-hidden" data-testid={`proveedor-card-${prov.rut_proveedor}`}>
                <div className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors">
                  <button onClick={() => toggleExpand(prov.rut_proveedor)} className="flex items-center gap-3 text-left flex-1 min-w-0" data-testid={`proveedor-toggle-${prov.rut_proveedor}`}>
                    {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" /> : <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />}
                    <Building2 className="w-5 h-5 text-blue-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-sm truncate">{prov.nombre_proveedor}</span>
                      <span className="text-xs text-muted-foreground font-mono ml-2">{prov.rut_proveedor}</span>
                    </div>
                  </button>
                  <div className="flex items-center gap-3 shrink-0 text-xs">
                    {prov.contabilizadas_count > 0 && <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {prov.contabilizadas_count} en Alegra</span>}
                    {prov.pendientes_count > 0 && <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded flex items-center gap-1"><Clock className="w-3 h-3" /> {prov.pendientes_count} pend.</span>}
                    {prov.en_lista_count > 0 && <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> {prov.en_lista_count} VB</span>}
                    <span className="font-bold text-gray-800 bg-gray-100 px-2 py-1 rounded">${fmt(prov.total_deuda)}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); imprimirEstadoCuenta(prov); }}
                      className="p-1.5 rounded hover:bg-blue-50 text-blue-700 hover:text-blue-900 border border-transparent hover:border-blue-200 transition-colors"
                      title="Imprimir estado de cuenta"
                      data-testid={`btn-print-prov-${prov.rut_proveedor}`}
                    >
                      <Printer className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); exportarEstadoCuentaExcel(prov); }}
                      className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700 hover:text-emerald-900 border border-transparent hover:border-emerald-200 transition-colors"
                      title="Exportar a Excel/CSV"
                      data-testid={`btn-excel-prov-${prov.rut_proveedor}`}
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t">
                    {/* Sub-summary */}
                    <div className="flex gap-4 px-4 py-2 bg-gray-50 text-[10px] text-gray-600 border-b">
                      {prov.saldo_alegra > 0 && <span>Saldo Alegra: <strong className="text-blue-700">${fmt(prov.saldo_alegra)}</strong></span>}
                      {prov.monto_pendiente_contab > 0 && <span>Pend. contab.: <strong className="text-amber-700">${fmt(prov.monto_pendiente_contab)}</strong></span>}
                      {prov.monto_en_lista > 0 && <span>En lista VB: <strong className="text-purple-700">${fmt(prov.monto_en_lista)}</strong></span>}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead><tr className="bg-gray-100 text-gray-600 text-left">
                          <th className="px-2 py-2 w-7"></th>
                          <th className="px-2 py-2">Estado</th>
                          <th className="px-2 py-2">N. Factura</th>
                          <th className="px-2 py-2">F. Emision</th>
                          <th className="px-2 py-2">F. Vencimiento</th>
                          <th className="px-2 py-2 text-right">Total</th>
                          <th className="px-2 py-2 text-right">Saldo</th>
                          <th className="px-2 py-2 text-center">OC</th>
                          <th className="px-2 py-2 text-center">Recepcion</th>
                          <th className="px-2 py-2">Forma Pago</th>
                          <th className="px-2 py-2 text-center">Doc</th>
                        </tr></thead>
                        <tbody>
                          {prov.facturas.map((f, fi) => {
                            const isAlegra = f.fuente === 'alegra';
                            const key = isAlegra ? `alegra-${f.alegra_id}` : `${f.numero_oc || 'sin'}|${f.docmto || f.numero_ref}`;
                            const isSelected = !isAlegra && f.estado === 'pendiente_contabilizar' && !!selected[`${f.numero_oc || ''}|${f.docmto || f.numero_ref}`];
                            const canSelect = !isAlegra && f.estado === 'pendiente_contabilizar';

                            const estadoOC = f.estado_oc || 'N/A';
                            const estadoRecep = f.estado_recepcion || 'N/A';
                            const fechaEmision = f.fecha_emision || '';
                            const fechaVencimiento = f.fecha_vencimiento || '';
                            const formaPago = f.forma_pago || '';

                            return (
                              <tr key={`${key}-${fi}`}
                                className={`border-t transition-colors ${isSelected ? 'bg-purple-50' : isAlegra ? 'bg-blue-50/30 hover:bg-blue-50' : f.estado === 'anulada_nc' ? 'bg-rose-50/40 hover:bg-rose-50 text-gray-500' : f.estado === 'en_lista_vb' ? 'bg-purple-50/30' : 'hover:bg-gray-50'}`}
                                data-testid={`factura-row-${key}`}>
                                <td className="px-2 py-2 text-center">
                                  {canSelect && (
                                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(f)}
                                      className="w-3.5 h-3.5 accent-purple-600 cursor-pointer" data-testid={`check-${f.numero_oc}-${f.docmto || f.numero_ref}`} />
                                  )}
                                </td>
                                <td className="px-2 py-2">
                                  {f.anulada_por_nc ? (
                                    <span
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-medium bg-rose-100 text-rose-800 border-rose-300"
                                      title={`Factura anulada por Nota de Crédito ${f.nc_folio || ''} (saldo $0)`}
                                      data-testid={`badge-anulada-nc-${f.numero_ref}`}
                                    >
                                      <XCircle className="w-3 h-3" /> Anulada por NC{f.nc_folio ? <> <DocRefLink tipo="dte" folio={f.nc_folio} rut={prov.rut_proveedor} label={f.nc_folio} icon={false} testId={`ec-nc-link-${prov.rut_proveedor}-${f.nc_folio}`} /></> : ''}
                                    </span>
                                  ) : (
                                    <EstadoBadge estado={f.estado} />
                                  )}
                                </td>
                                <td className="px-2 py-2 font-mono font-bold">
                                  {(f.numero_ref || f.docmto)
                                    ? <DocRefLink tipo="dte" folio={f.numero_ref || f.docmto} rut={prov.rut_proveedor} label={f.numero_ref || f.docmto} testId={`ec-dte-link-${prov.rut_proveedor}-${f.numero_ref || f.docmto}`} />
                                    : <span className="text-gray-400">-</span>}
                                </td>
                                <td className="px-2 py-2 text-gray-600 whitespace-nowrap">
                                  {fechaEmision || '-'}
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap">
                                  {fechaVencimiento ? (
                                    <span className={new Date(fechaVencimiento) < new Date() ? 'text-red-600 font-medium' : 'text-gray-600'}>
                                      {fechaVencimiento}
                                    </span>
                                  ) : <span className="text-gray-300">-</span>}
                                </td>
                                <td className="px-2 py-2 text-right font-mono font-medium">
                                  ${fmt(f.total)}
                                </td>
                                <td className="px-2 py-2 text-right font-mono">
                                  <span className={f.saldo > 0 ? 'text-red-700 font-medium' : 'text-green-700'}>${fmt(f.saldo)}</span>
                                </td>
                                <td className="px-2 py-2 text-center">
                                  {estadoOC === 'Con OC' ? (
                                    <span className="inline-flex items-center gap-0.5 text-green-700 bg-green-50 px-1.5 py-0.5 rounded text-[10px]">
                                      <CheckCircle className="w-3 h-3" /> <DocRefLink tipo="oc" numero={f.numero_oc} label={f.numero_oc} icon={false} testId={`ec-oc-link-${prov.rut_proveedor}-${f.numero_oc}`} />
                                    </span>
                                  ) : estadoOC === 'Sin OC' ? (
                                    <span className="text-amber-600 text-[10px]">Sin OC</span>
                                  ) : (
                                    <span className="text-gray-300 text-[10px]">-</span>
                                  )}
                                </td>
                                <td className="px-2 py-2 text-center">
                                  {estadoRecep === 'Recepcionada' ? (
                                    <span className="inline-flex items-center gap-0.5 text-green-700 bg-green-50 px-1.5 py-0.5 rounded text-[10px]">
                                      <CheckCircle className="w-3 h-3" /> Si
                                    </span>
                                  ) : estadoRecep === 'Pendiente' ? (
                                    <span className="inline-flex items-center gap-0.5 text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded text-[10px]">
                                      <Clock className="w-3 h-3" /> Pend.
                                    </span>
                                  ) : (
                                    <span className="text-gray-300 text-[10px]">-</span>
                                  )}
                                </td>
                                <td className="px-2 py-2 text-[10px] text-gray-600 whitespace-nowrap">
                                  {formaPago || '-'}
                                </td>
                                <td className="px-2 py-2 text-center">
                                  {f.ruta_archivo ? (() => {
                                    const fn = f.ruta_archivo.split('\\').pop().split('/').pop();
                                    return <button onClick={() => onViewDocument(fn)} className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 text-[9px] bg-blue-50 px-1.5 py-0.5 rounded"><Eye className="w-3 h-3" /></button>;
                                  })() : <span className="text-gray-300">-</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot><tr className="bg-gray-50 border-t font-medium text-xs">
                          <td colSpan={5} className="px-2 py-2 text-right text-gray-600">Total {prov.nombre_proveedor}:</td>
                          <td className="px-2 py-2 text-right font-mono font-bold">${fmt(prov.facturas.reduce((s, f) => s + (f.estado === 'anulada_nc' ? 0 : (f.total || 0)), 0))}</td>
                          <td className="px-2 py-2 text-right font-mono font-bold text-red-700">${fmt(prov.total_deuda)}</td>
                          <td colSpan={4}></td>
                        </tr></tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && data && filteredProveedores.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-600 font-medium">No se encontraron proveedores</p>
          <p className="text-sm text-muted-foreground mt-1">{searchTerm ? 'Intente con otro termino' : 'No hay facturas pendientes de pago'}</p>
        </div>
      )}
    </>
  );
}


// =========== MAIN PAGE ===========
export default function ContabilizarOC() {
  const [activeTab, setActiveTab] = useState('estado_cuenta');
  const [docModal, setDocModal] = useState({ open: false, loading: false, data: null, error: null });

  const handleViewDocument = useCallback(async (filename) => {
    setDocModal({ open: true, loading: true, data: null, error: null });
    try {
      const res = await api.get(`/sharepoint/buscar-documento?filename=${encodeURIComponent(filename)}`);
      if (res.data.found) { setDocModal({ open: true, loading: false, data: res.data, error: null }); }
      else { setDocModal({ open: true, loading: false, data: { found: false, filename, sharepointFolderUrl: 'https://chirinosteelcom.sharepoint.com/:f:/s/ServidorChsteel/IgCijncVJt1bRZy1oVHJFg41Ae24HqfThKmBpQ_OMKYpy2w?e=KUf46I' }, error: null }); }
    } catch (err) {
      setDocModal({ open: true, loading: false, data: { found: false, filename, sharepointFolderUrl: 'https://chirinosteelcom.sharepoint.com/:f:/s/ServidorChsteel/IgCijncVJt1bRZy1oVHJFg41Ae24HqfThKmBpQ_OMKYpy2w?e=KUf46I', apiError: err.response?.data?.detail || 'Error conectando con SharePoint' }, error: null });
    }
  }, []);

  return (
    <div className="space-y-5" data-testid="contabilizar-oc-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <List className="w-6 h-6 text-blue-600" />
          Estado Cuenta Proveedor
        </h1>
        <p className="text-sm text-muted-foreground">
          Estado de cuenta por proveedor: facturas pendientes de pago y pendientes de contabilizar.
        </p>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit" data-testid="tab-switcher">
        <button onClick={() => setActiveTab('estado_cuenta')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'estado_cuenta' ? 'bg-white shadow-sm text-blue-700' : 'text-gray-600 hover:text-gray-900'}`}
          data-testid="tab-estado-cuenta">
          <Building2 className="w-4 h-4" /> Estado de Cuenta
        </button>
        <button onClick={() => setActiveTab('oc')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'oc' ? 'bg-white shadow-sm text-blue-700' : 'text-gray-600 hover:text-gray-900'}`}
          data-testid="tab-por-oc">
          <FileText className="w-4 h-4" /> Por N. OC
        </button>
      </div>

      {activeTab === 'oc' && <TabPorOC onViewDocument={handleViewDocument} />}
      {activeTab === 'estado_cuenta' && <TabEstadoCuenta onViewDocument={handleViewDocument} />}

      {/* Document Viewer Modal */}
      <Dialog open={docModal.open} onOpenChange={(open) => !docModal.loading && setDocModal({ ...docModal, open })}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" /> {docModal.data?.found ? docModal.data.filename : 'Documento'}</DialogTitle></DialogHeader>
          {docModal.loading && <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /><span className="ml-2 text-gray-600">Buscando en SharePoint...</span></div>}
          {docModal.data && !docModal.data.found && (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded p-4 text-yellow-800"><AlertTriangle className="w-5 h-5 inline mr-2" /><strong>{docModal.data.filename}</strong>{docModal.data.apiError && <p className="mt-2 text-sm">{docModal.data.apiError}</p>}</div>
              <div className="text-center py-4"><FileText className="w-16 h-16 mx-auto mb-3 text-gray-300" /><p className="text-gray-600 mb-4">El documento puede estar en SharePoint.</p>
                <Button onClick={() => window.open(docModal.data.sharepointFolderUrl, '_blank')} className="flex items-center gap-2 mx-auto bg-blue-600 hover:bg-blue-700"><ExternalLink className="w-4 h-4" /> Abrir Carpeta en SharePoint</Button></div>
            </div>
          )}
          {docModal.data?.found && (
            <div className="space-y-4">
              {docModal.data.previewUrl ? <div className="border rounded-lg overflow-hidden bg-gray-100" style={{ height: '500px' }}><iframe src={docModal.data.previewUrl} className="w-full h-full" title="Vista previa" /></div>
               : <div className="bg-gray-100 rounded-lg p-8 text-center text-gray-500"><FileText className="w-16 h-16 mx-auto mb-3 text-gray-300" /><p>Vista previa no disponible</p></div>}
              <div className="flex gap-3 justify-center">
                {docModal.data.webUrl && <Button variant="outline" onClick={() => window.open(docModal.data.webUrl, '_blank')}><ExternalLink className="w-4 h-4 mr-2" /> Abrir en SharePoint</Button>}
                <Button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sharepoint/descargar/${docModal.data.file_id}`, '_blank')} className="bg-blue-600 hover:bg-blue-700"><FileDown className="w-4 h-4 mr-2" /> Descargar PDF</Button>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDocModal({ open: false, loading: false, data: null, error: null })}>Cerrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
